import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Chip,
  ChipProps,
  Grid,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import { useAppSelector } from '../../../../hooks';
import { getQuestionDifficultyChipColor } from '../../../../helper';
import { TP_COLORS } from '../../../../constants';

import { ISubTopic } from '../../../../store/SubTopicSlice';
import { getQuestionState, IQuestion } from '../../../../store/QuestionSlice';

import AnswerListItem from './AnswerListItem';

const titleTypoProps: TypographyProps = {
  variant: 'h5',
  fontWeight: '600',
  marginBottom: '40px',
  sx: { fontSize: { xs: '16px', sm: '22px', md: '25px' } },
};

const boxProps: BoxProps = {
  padding: '20px',
  sx: {
    borderRadius: '15px',
    background: TP_COLORS.white,
  },
};

const topStackProps: StackProps = {
  direction: 'row',
  justifyContent: 'space-between',
  marginBottom: '20px',
};

const chipStyles: ChipProps['style'] = {
  height: '27px',
  fontWeight: 600,
  borderRadius: '5px',
};

const marksChipStyles: ChipProps['style'] = {
  ...chipStyles,
  backgroundColor: TP_COLORS.gray.dark,
  color: TP_COLORS.white,
  marginLeft: '10px',
};

const questionNoStackProps: StackProps = {
  flexDirection: 'row',
  justifyContent: 'center',
  position: 'relative',
  top: '35px',
  sx: { display: { xs: 'none', sm: 'flex' } },
};

const bottomStackProps: StackProps = {
  flexDirection: 'row',
  justifyContent: 'flex-end',
  sx: { marginTop: { xs: '20px', sm: '0px' } },
};

const questionNoMobileProps: StackProps = {
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  sx: { display: { xs: 'flex', sm: 'none' } },
};

const buttonDefaultStyles = {
  fontWeight: 600,
  height: '45px',
  width: '110px',
  marginLeft: '10px',
};

const primaryBtnProps: ButtonProps = {
  variant: 'contained',
  color: 'primary',
  sx: buttonDefaultStyles,
};

const secondaryBtnProps: ButtonProps = {
  variant: 'outlined',
  color: 'inherit',
  sx: {
    ...buttonDefaultStyles,
    color: TP_COLORS.gray.darken,
    borderColor: TP_COLORS.gray.darken,
    backgroundColor: '#F8FAFC',
  },
};

interface IPracticeQuestionsProps {
  subTopicData: ISubTopic;
}

const PracticeQuestions: React.FC<IPracticeQuestionsProps> = ({
  subTopicData,
}) => {
  // Store
  const questionState = useAppSelector(getQuestionState);

  const { list: questionList } = questionState;

  // State
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [selectedAnswerId, setSelectedAnswerId] = useState<string>('');
  const [selectedQuestion, setSelectedQuestion] = useState<
    IQuestion | undefined
  >(undefined);

  const selectedQuestionIndex =
    questionList.length && selectedQuestion
      ? questionList.findIndex(
          (questionItem) => questionItem._id === selectedQuestion._id
        )
      : -1;

  useEffect(() => {
    if (questionList.length) {
      setSelectedQuestion(questionList[0]);
    }
  }, [questionList]);

  const handleNext = () => {
    if (
      questionList.length &&
      selectedQuestionIndex < questionList.length - 1
    ) {
      setSelectedQuestion(questionList[selectedQuestionIndex + 1]);
      resetQuestion();
    }
  };

  const handlePrev = () => {
    if (questionList.length && selectedQuestionIndex > 0) {
      setSelectedQuestion(questionList[selectedQuestionIndex - 1]);
      resetQuestion();
    }
  };

  const resetQuestion = () => {
    setIsSubmitted(false);
    setSelectedAnswerId('');
  };

  return (
    <Box marginBottom="40px">
      <Typography {...titleTypoProps}>
        Practice question for {subTopicData.name}
      </Typography>
      {selectedQuestion && (
        <Box {...boxProps}>
          <Stack {...topStackProps}>
            <Chip
              label={`Question ${selectedQuestionIndex + 1}`}
              style={{
                ...chipStyles,
                backgroundColor: TP_COLORS.border.color1,
                color: TP_COLORS.gray.darken,
              }}
            />
            <Box>
              <Chip
                label={selectedQuestion.difficulty}
                color={getQuestionDifficultyChipColor(
                  selectedQuestion.difficulty
                )}
                style={chipStyles}
              />
              <Chip
                label={`${selectedQuestion.marks} Mark(s)`}
                style={marksChipStyles}
              />
            </Box>
          </Stack>
          <div
            className="view ql-editor ql-viewer"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: (selectedQuestion.details || '').trim(),
            }}
          />

          <Grid container spacing={2} padding="14px 20px 20px 20px">
            {selectedQuestion.answers.map((answer, index) => (
              <Grid key={index} item xs={12}>
                <AnswerListItem
                  answer={answer}
                  showAnswer={isSubmitted}
                  markingSchema={selectedQuestion.markingSchema}
                  onClick={(value) =>
                    isSubmitted ? undefined : setSelectedAnswerId(value)
                  }
                  selectedAnswerId={selectedAnswerId}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Stack {...questionNoStackProps}>
        <Typography variant="subtitle2" fontWeight="600">
          {selectedQuestionIndex + 1} of {questionList.length}
        </Typography>
      </Stack>
      <Stack {...bottomStackProps}>
        <Box {...questionNoMobileProps}>
          <Typography variant="subtitle2" fontWeight="600">
            {selectedQuestionIndex + 1} of {questionList.length}
          </Typography>
        </Box>

        <Box display="flex">
          {questionList.length && selectedQuestionIndex > 0 ? (
            <Button {...secondaryBtnProps} onClick={handlePrev}>
              Prev
            </Button>
          ) : null}

          {isSubmitted &&
          questionList.length &&
          selectedQuestionIndex < questionList.length - 1 ? (
            <Button {...secondaryBtnProps} onClick={handleNext}>
              Next
            </Button>
          ) : null}

          {!isSubmitted && (
            <Button {...primaryBtnProps} onClick={() => setIsSubmitted(true)}>
              Submit
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default PracticeQuestions;
